/* Product Page: Gallery, Variants, Quantity, Cart, Recommendations */
(function () {
  const $ = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => [...(c || document).querySelectorAll(s)];
  const cartDrawerSectionId = () =>
    document.querySelector("[data-cart-drawer]")?.dataset.cartSectionId || "";
  const currentSectionsUrl = () =>
    `${window.location.pathname}${window.location.search}` || "/";
  const formatMoney = window.DutchXXLTheme.formatMoney;
  const afterElementMotion = (element, callback) => {
    const finish = () => callback?.();
    window.requestAnimationFrame(() => {
      const animations = element?.getAnimations?.().filter(
        (animation) => animation.playState !== "finished",
      );
      if (!animations?.length) {
        finish();
        return;
      }
      Promise.allSettled(animations.map((animation) => animation.finished)).then(
        finish,
      );
    });
  };

  const STORE_TIMEZONE = "Europe/Amsterdam";
  const DAY_NAMES = [
    "zondag",
    "maandag",
    "dinsdag",
    "woensdag",
    "donderdag",
    "vrijdag",
    "zaterdag",
  ];
  const LALEE_SKU_PATTERN = /^[A-Z]{3}[0-9]{3}[a-z]+[0-9]+$/;
  const storeTimeFormatter =
    typeof Intl !== "undefined" && Intl.DateTimeFormat
      ? new Intl.DateTimeFormat("en-US", {
          timeZone: STORE_TIMEZONE,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
        })
      : null;

  const getStoreNow = () => {
    if (!storeTimeFormatter?.formatToParts) return new Date();
    try {
      const parts = storeTimeFormatter.formatToParts(new Date());
      const get = (type) =>
        parseInt(parts.find((p) => p.type === type)?.value || "0", 10);
      return new Date(
        Date.UTC(
          get("year"),
          get("month") - 1,
          get("day"),
          get("hour"),
          get("minute"),
          get("second"),
        ),
      );
    } catch {
      return new Date();
    }
  };

  // Axis-locked swipe helper for complementary slider
  const bindAxisLockedSwipe = (el, onSwipe) => {
    let startX = 0,
      startY = 0,
      locked = null;
    const LOCK = 10,
      SWIPE = 50;
    el.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        locked = null;
      },
      { passive: true },
    );
    el.addEventListener(
      "touchmove",
      (e) => {
        if (locked === "y") return;
        const dx = e.touches[0].clientX - startX,
          dy = e.touches[0].clientY - startY;
        if (!locked && (Math.abs(dx) > LOCK || Math.abs(dy) > LOCK))
          locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (locked === "x") e.preventDefault();
      },
      { passive: false },
    );
    el.addEventListener(
      "touchend",
      (e) => {
        if (locked !== "x") return;
        const dx = startX - e.changedTouches[0].clientX;
        if (Math.abs(dx) > SWIPE) onSwipe(dx > 0 ? 1 : -1);
      },
      { passive: true },
    );
  };

  const calcDeliveryDayName = (businessDays) => {
    const now = getStoreNow();
    let start = new Date(now);
    if (start.getUTCHours() >= 22) start.setUTCDate(start.getUTCDate() + 1);
    const startDow = start.getUTCDay();
    if (startDow === 0) start.setUTCDate(start.getUTCDate() + 1);
    else if (startDow === 6) start.setUTCDate(start.getUTCDate() + 2);
    else if (startDow === 5) start.setUTCDate(start.getUTCDate() + 3);
    let delivery = new Date(start);
    let remaining = businessDays;
    while (remaining > 0) {
      delivery.setUTCDate(delivery.getUTCDate() + 1);
      const dow = delivery.getUTCDay();
      if (dow !== 0 && dow !== 6) remaining--;
    }
    return DAY_NAMES[delivery.getUTCDay()];
  };

  const calcBusinessDays = (vendor, sku) => {
    // ETA days counted AFTER start day: 1=2nd business day, 2=3rd business day, etc.
    if (vendor === "Lalee") return 8;
    if (vendor === "Kayoom") return LALEE_SKU_PATTERN.test(sku || "") ? 8 : 2;
    if (vendor === "Ayyildiz") return 2;
    return 1;
  };

  const calcDeliveryPromiseDays = (vendor, sku) => {
    if (vendor === "Lalee") return 8;
    if (vendor === "Kayoom") return LALEE_SKU_PATTERN.test(sku || "") ? 8 : 2;
    return 1;
  };

  const shouldShowDeliveryLine = (vendor, sku) => {
    if (vendor === "Lalee") return false;
    if (vendor === "Ayyildiz") return true;
    if (vendor === "Kayoom") return !LALEE_SKU_PATTERN.test(sku || "");
    return false;
  };

  const getDeliveryText = (days, el) => {
    if (!el) return "";
    const textForDays = (value) =>
      el.dataset[`deliveryText${value}`] ||
      el.dataset[`deliveryText-${value}`] ||
      el.getAttribute(`data-delivery-text-${value}`);
    // Trust bar text mapping (intentionally different from exact weekday calculation)
    // promise=1 (Ayyildiz/default) = "1 tot 3", promise=2 (Kayoom) = "1 tot 5", promise=8 (Lalee) = "1 tot 8"
    const map = {
      1: textForDays(1), // "1 tot 3 werkdagen"
      2: textForDays(2), // "1 tot 5 werkdagen"
      8: textForDays(8), // "1 tot 8 werkdagen"
    };
    return (
      map[days] || el.textContent || "Binnen 1 tot 3 werkdagen bij jou thuis."
    );
  };

  class ProductGallery {
    constructor(c) {
      const q = (s) => $(s, c),
        qa = (s) => $$(s, c);
      Object.assign(this, {
        c,
        viewport: q("[data-gallery-viewport]"),
        track: q("[data-gallery-track]"),
        prevBtn: q("[data-gallery-prev]"),
        nextBtn: q("[data-gallery-next]"),
        thumbs: qa("[data-thumbnail]"),
        slides: qa("[data-gallery-slide]"),
        indCur: q("[data-indicator-current]"),
        indIcon: q("[data-indicator-icon]"),
        idx: parseInt(
          q("[data-gallery-viewport]")?.dataset.initialIndex || "0",
          10,
        ),
      });
      this.total = this.slides.length;
      this.preloaded = new Set(); // Track preloaded images
      this.preloading = new Map();
      if (this.viewport && this.total) this.init();
    }

    init() {
      this.prevBtn?.addEventListener("click", () => this.go(this.idx - 1));
      this.nextBtn?.addEventListener("click", () => this.go(this.idx + 1));
      this.thumbs.forEach((t, i) => {
        t.addEventListener("click", () => this.go(i));
        t.addEventListener("pointerdown", () => this.prepareSlide(i, "high"), {
          passive: true,
        });
        t.addEventListener("pointerenter", () => this.prepareSlide(i), {
          passive: true,
        });
        t.addEventListener("focus", () => this.prepareSlide(i));
      });
      this.swipe();
      this.ui();
      // Preload adjacent slides on init
      this.preloadAdjacent();
    }

    go(i) {
      const targetIdx = Math.max(0, Math.min(i, this.total - 1));
      if (targetIdx === this.idx) {
        this.track.style.transform = `translateX(-${this.idx * 100}%)`;
        return;
      }

      this.prepareSlide(targetIdx, "high");

      this.track.classList.add("product-gallery__track--animating");
      const finishAnimation = () =>
        this.track.classList.remove("product-gallery__track--animating");

      this.idx = targetIdx;
      this.track.style.transform = `translateX(-${this.idx * 100}%)`;
      this.ui();
      this.preloadAdjacent();
      afterElementMotion(this.track, finishAnimation);
    }

    ui() {
      const {
        idx,
        total,
        thumbs,
        slides,
        indCur,
        indIcon,
        prevBtn,
        nextBtn,
      } = this;
      thumbs.forEach((t, i) => {
        const isActive = i === idx;
        t.classList.toggle("is-active", isActive);
        if (isActive) t.setAttribute("aria-current", "true");
        else t.removeAttribute("aria-current");
      });
      slides.forEach((slide, i) =>
        slide.classList.toggle("is-active", i === idx),
      );
      if (indCur) indCur.textContent = idx + 1;
      if (indIcon) {
        const t = slides[idx]?.dataset.mediaType;
        indIcon.style.display =
          t === "video" || t === "external_video" ? "inline-flex" : "none";
      }
      prevBtn?.toggleAttribute("disabled", idx === 0);
      nextBtn?.toggleAttribute("disabled", idx === total - 1);
      this.scrollThumb();
    }

    scrollThumb() {
      const thumb = this.thumbs[this.idx],
        cont = thumb?.closest("[data-gallery-thumbnails]");
      if (!cont) return;
      const cr = cont.getBoundingClientRect(),
        tr = thumb.getBoundingClientRect();
      let left = cont.scrollLeft,
        top = cont.scrollTop,
        shouldScroll = false;

      if (tr.left < cr.left || tr.right > cr.right) {
        left += tr.left - cr.left - (cr.width - tr.width) / 2;
        shouldScroll = true;
      }
      if (tr.top < cr.top || tr.bottom > cr.bottom) {
        top += tr.top - cr.top - (cr.height - tr.height) / 2;
        shouldScroll = true;
      }

      // Keep thumbnail syncing inside its own scroller; scrollIntoView also
      // moves the document when an off-screen end thumbnail is selected.
      if (shouldScroll) cont.scrollTo({ left, top, behavior: "smooth" });
    }

    // Start loading/decoding a slide image without blocking navigation feedback.
    prepareSlide(i, priority = "auto") {
      if (i < 0 || i >= this.total) return Promise.resolve();
      const slide = this.slides[i];
      const img = slide?.querySelector("img");
      if (!img) return Promise.resolve();

      if ("fetchPriority" in img) img.fetchPriority = priority;
      else img.setAttribute("fetchpriority", priority);

      if (img.loading === "lazy") img.loading = "eager";

      if (this.preloaded.has(i) && img.complete) return Promise.resolve();
      if (this.preloading.has(i)) return this.preloading.get(i);

      if (img.complete) {
        this.preloaded.add(i);
        return Promise.resolve();
      }

      const promise =
        typeof img.decode === "function"
          ? img.decode().catch(() => {})
          : new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            });

      const tracked = promise.finally(() => {
        this.preloaded.add(i);
        this.preloading.delete(i);
      });
      this.preloading.set(i, tracked);
      return tracked;
    }

    // Preload slides adjacent to current index (fire-and-forget for background preload)
    preloadAdjacent() {
      this.prepareSlide(this.idx - 1);
      this.prepareSlide(this.idx + 1);
    }

    swipe() {
      const el = this.viewport;
      let startX = 0,
        startY = 0,
        locked = null,
        dragX = 0;
      const LOCK = 10,
        SWIPE = 50;
      const vw = () => el.offsetWidth;

      el.addEventListener(
        "touchstart",
        (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          locked = null;
          dragX = 0;
          // Disable transition for immediate response
          this.track.classList.add(
            "product-gallery__track--dragging",
            "product-gallery__track--no-transition",
          );
        },
        { passive: true },
      );

      el.addEventListener(
        "touchmove",
        (e) => {
          if (locked === "y") return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;
          if (!locked && (Math.abs(dx) > LOCK || Math.abs(dy) > LOCK)) {
            locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
          }
          if (locked === "x") {
            e.preventDefault();
            const isOutOfBoundsDrag =
              (this.idx === 0 && dx > 0) ||
              (this.idx === this.total - 1 && dx < 0);
            if (isOutOfBoundsDrag) {
              dragX = 0;
              this.track.style.transform = `translateX(-${this.idx * 100}%)`;
              return;
            }

            dragX = dx;
            const baseOffset = -this.idx * 100;
            const dragPct = (dx / vw()) * 100;
            this.track.style.transform = `translateX(calc(${baseOffset}% + ${dragPct}%))`;
          }
        },
        { passive: false },
      );

      el.addEventListener(
        "touchend",
        () => {
          // Re-enable transition for snap animation
          this.track.classList.remove(
            "product-gallery__track--dragging",
            "product-gallery__track--no-transition",
          );
          if (locked !== "x") return;
          // Determine if swipe threshold met
          if (Math.abs(dragX) > SWIPE) {
            this.go(this.idx + (dragX < 0 ? 1 : -1));
          } else {
            // Snap back to current slide
            this.track.style.transform = `translateX(-${this.idx * 100}%)`;
          }
        },
        { passive: true },
      );
    }
  }

  class ProductLightbox {
    constructor(galleryEl) {
      this.gallery = galleryEl;
      this.lightbox = $("[data-product-lightbox]");
      if (!this.lightbox) return;

      Object.assign(this, {
        container: $(".lightbox__container", this.lightbox),
        overlay: $(".lightbox__overlay", this.lightbox),
        track: $("[data-lightbox-track]", this.lightbox),
        viewport: $("[data-lightbox-viewport]", this.lightbox),
        slides: $$("[data-lightbox-slide]", this.lightbox),
        thumbs: $$("[data-lightbox-thumb]", this.lightbox),
        prevBtn: $("[data-lightbox-prev]", this.lightbox),
        nextBtn: $("[data-lightbox-next]", this.lightbox),
        closeBtns: $$("[data-lightbox-close]", this.lightbox),
        currentEl: $("[data-lightbox-current]", this.lightbox),
        idx: 0,
        isOpen: false,
        ac: null,
        loadingSlides: new Map(),
        focusedBefore: null,
      });

      this.total = this.slides.length;
      if (!this.total) return;
      this.lightboxMq = window.matchMedia("(min-width: 768px)");

      this.mediaIndexToSlide = new Map(
        this.slides.map((slide, i) => [
          parseInt(slide.dataset.index || i, 10),
          i,
        ]),
      );
      this.bindTriggers();
      this.syncTriggerAccessibility();
      const onBreakpointChange = () => {
        if (!this.canOpen() && this.isOpen) this.close();
        this.syncTriggerAccessibility();
      };
      if (this.lightboxMq.addEventListener) {
        this.lightboxMq.addEventListener("change", onBreakpointChange);
      } else if (this.lightboxMq.addListener) {
        this.lightboxMq.addListener(onBreakpointChange);
      }
    }

    get triggerElements() {
      return $$("[data-lightbox-trigger]", this.gallery);
    }

    canOpen() {
      return this.lightboxMq?.matches === true;
    }

    slideIndexFromMediaIndex(mediaIndex) {
      if (Number.isFinite(mediaIndex) && this.mediaIndexToSlide.has(mediaIndex))
        return this.mediaIndexToSlide.get(mediaIndex);
      return Math.max(0, Math.min(mediaIndex || 0, this.total - 1));
    }

    triggerLabel(trigger) {
      const mediaIndex = parseInt(trigger.dataset.lightboxTrigger, 10);
      const ordinal = parseInt(trigger.dataset.lightboxOrdinal, 10);
      const fallbackIndex = this.triggerElements.indexOf(trigger);
      const displayIndex = Number.isFinite(ordinal)
        ? ordinal
        : Number.isFinite(mediaIndex)
          ? this.slideIndexFromMediaIndex(mediaIndex) + 1
          : fallbackIndex + 1;
      return `Vergroot afbeelding ${displayIndex} van ${this.total}`;
    }

    syncTriggerAccessibility() {
      const enabled = this.canOpen();
      this.triggerElements.forEach((trigger) => {
        if (enabled) {
          trigger.setAttribute("role", "button");
          trigger.setAttribute("tabindex", "0");
          trigger.setAttribute("aria-label", this.triggerLabel(trigger));
        } else {
          if (trigger.getAttribute("role") === "button")
            trigger.removeAttribute("role");
          trigger.removeAttribute("tabindex");
          trigger.removeAttribute("aria-label");
        }
      });
    }

    bindTriggers() {
      this.gallery.addEventListener("click", (e) => {
        const trigger = e.target.closest("[data-lightbox-trigger]");
        if (trigger) {
          if (!this.canOpen()) return;
          e.preventDefault();
          this.activateTrigger(trigger, { restoreFocus: false });
        }
      });
      this.gallery.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        const trigger = e.target.closest("[data-lightbox-trigger]");
        if (trigger) {
          if (!this.canOpen()) return;
          e.preventDefault();
          this.activateTrigger(trigger, { restoreFocus: true });
        }
      });
    }

    activateTrigger(trigger, { restoreFocus = false } = {}) {
      if (!this.canOpen()) return;
      const mediaIndex = parseInt(trigger.dataset.lightboxTrigger, 10);
      this.open(this.slideIndexFromMediaIndex(mediaIndex), { restoreFocus });
    }

    open(idx = 0, { restoreFocus = true } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.idx = Math.max(0, Math.min(idx, this.total - 1));

      this.restoreFocusOnClose = restoreFocus;
      this.focusedBefore = restoreFocus ? document.activeElement : null;
      this.ac = new AbortController();
      const { signal } = this.ac;

      document.addEventListener("keydown", this.onKeydown, { signal });
      this.closeBtns.forEach((btn) =>
        btn.addEventListener("click", () => this.close(), { signal }),
      );
      this.prevBtn?.addEventListener("click", () => this.go(this.idx - 1), {
        signal,
      });
      this.nextBtn?.addEventListener("click", () => this.go(this.idx + 1), {
        signal,
      });
      this.thumbs.forEach((t, i) => {
        const mediaIndex = parseInt(t.dataset.lightboxThumb, 10);
        const slideIdx = this.slideIndexFromMediaIndex(mediaIndex || i);
        t.addEventListener("click", () => this.go(slideIdx), { signal });
        t.addEventListener(
          "pointerdown",
          () => this.loadSlide(slideIdx, "high"),
          {
            passive: true,
            signal,
          },
        );
        t.addEventListener("pointerenter", () => this.loadSlide(slideIdx), {
          passive: true,
          signal,
        });
        t.addEventListener("focus", () => this.loadSlide(slideIdx), { signal });
      });
      this.container?.addEventListener("click", (e) => e.stopPropagation(), {
        signal,
      });

      this.bindSwipe(signal);

      document.body.classList.add("modal-open");

      this.loadSlide(this.idx, "high");
      this.track.style.transform = `translateX(-${this.idx * 100}%)`;

      this.lightbox.style.display = "flex";
      this.updateUI({ scroll: false });
      this.lightbox.offsetHeight; // Force reflow
      this.lightbox.classList.add("lightbox--open");

      this.loadAdjacent();

      const closeBtn = $(".lightbox__close", this.lightbox);
      closeBtn?.focus();
    }

    close() {
      if (!this.isOpen) return;

      this.lightbox.classList.remove("lightbox--open");
      this.lightbox.classList.add("lightbox--closing");

      afterElementMotion(this.container, () => {
        this.lightbox.style.display = "none";
        this.lightbox.classList.remove("lightbox--closing");
        document.body.classList.remove("modal-open");
        this.isOpen = false;

        this.ac?.abort();
        this.ac = null;

        if (this.restoreFocusOnClose && this.focusedBefore?.isConnected) {
          this.focusedBefore.focus({ preventScroll: true });
        } else if (this.lightbox.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        this.focusedBefore = null;
        this.restoreFocusOnClose = false;
      });
    }

    go(i) {
      const targetIdx = Math.max(0, Math.min(i, this.total - 1));

      this.loadSlide(targetIdx, "high");
      if (targetIdx === this.idx) {
        this.track.style.transform = `translateX(-${this.idx * 100}%)`;
        return;
      }

      this.idx = targetIdx;
      this.track.style.transform = `translateX(-${this.idx * 100}%)`;
      this.updateUI();
      this.loadAdjacent();
    }

    updateUI({ scroll = true } = {}) {
      const { idx, total, thumbs, currentEl, prevBtn, nextBtn } = this;

      if (currentEl) currentEl.textContent = idx + 1;
      thumbs.forEach((t, i) => {
        const isActive = i === idx;
        t.classList.toggle("is-active", isActive);
        if (isActive) t.setAttribute("aria-current", "true");
        else t.removeAttribute("aria-current");
      });
      prevBtn?.toggleAttribute("disabled", idx === 0);
      nextBtn?.toggleAttribute("disabled", idx === total - 1);

      const activeThumb = thumbs[idx];
      if (scroll)
        activeThumb?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
    }

    loadSlide(i, priority = "auto") {
      if (i < 0 || i >= this.total) return Promise.resolve();

      const slide = this.slides[i];
      const img = slide?.querySelector(".lightbox__image");

      if (!img) return Promise.resolve();

      if ("fetchPriority" in img) img.fetchPriority = priority;
      else img.setAttribute("fetchpriority", priority);
      if (img.loading === "lazy") img.loading = "eager";

      if (this.loadingSlides.has(i)) return this.loadingSlides.get(i);

      const hasDeferredSource = img.dataset.src || img.dataset.srcset;
      if (!hasDeferredSource && img.complete) return Promise.resolve();

      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute("data-srcset");
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute("data-src");
      }

      const waitForLoad =
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              const done = () => {
                img.removeEventListener("load", done);
                img.removeEventListener("error", done);
                resolve();
              };
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
            });

      const tracked = waitForLoad
        .then(() => {
          if (img.naturalWidth <= 0) return;
          return typeof img.decode === "function"
            ? img.decode().catch(() => {})
            : null;
        })
        .finally(() => {
          this.loadingSlides.delete(i);
        });

      this.loadingSlides.set(i, tracked);
      return tracked;
    }

    loadAdjacent() {
      // Fire-and-forget for adjacent slides
      this.loadSlide(this.idx - 1);
      this.loadSlide(this.idx + 1);
    }

    onKeydown = (e) => {
      switch (e.key) {
        case "Escape":
          this.close();
          break;
        case "ArrowLeft":
          e.preventDefault();
          this.go(this.idx - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          this.go(this.idx + 1);
          break;
        case "Tab":
          this.trapFocus(e);
          break;
      }
    };

    trapFocus(e) {
      const focusable = this.lightbox.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first?.focus();
        e.preventDefault();
      }
    }

    bindSwipe(signal) {
      const el = this.viewport;
      let startX = 0,
        startY = 0,
        locked = null,
        dragX = 0;
      const LOCK = 10,
        SWIPE = 50;

      const onStart = (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        locked = null;
        dragX = 0;
        this.track.classList.add("lightbox__track--no-transition");
      };

      const onMove = (e) => {
        if (locked === "y") return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        if (!locked && (Math.abs(dx) > LOCK || Math.abs(dy) > LOCK)) {
          locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        }

        if (locked === "x") {
          e.preventDefault();
          const isOutOfBoundsDrag =
            (this.idx === 0 && dx > 0) ||
            (this.idx === this.total - 1 && dx < 0);
          if (isOutOfBoundsDrag) {
            dragX = 0;
            this.track.style.transform = `translateX(-${this.idx * 100}%)`;
            return;
          }

          dragX = dx;
          const baseOffset = -this.idx * 100;
          const dragPct = (dx / el.offsetWidth) * 100;

          this.track.style.transform = `translateX(calc(${baseOffset}% + ${dragPct}%))`;
        }
      };

      const onEnd = () => {
        this.track.classList.remove("lightbox__track--no-transition");
        if (locked !== "x") return;

        if (Math.abs(dragX) > SWIPE) {
          this.go(this.idx + (dragX < 0 ? 1 : -1));
        } else {
          this.track.style.transform = `translateX(-${this.idx * 100}%)`;
        }
      };

      el.addEventListener("touchstart", onStart, { passive: true, signal });
      el.addEventListener("touchmove", onMove, { passive: false, signal });
      el.addEventListener("touchend", onEnd, { passive: true, signal });
    }
  }

  class VariantSelector {
    constructor(form) {
      this.form = form;
      this.sel = $("[data-product-select]", form);
      if (!this.sel) return;

      const parse = (s) => {
        try {
          return JSON.parse($(s)?.textContent || "{}");
        } catch {
          return {};
        }
      };
      this.prod = parse("[data-product-json]");
      this.inv = parse("[data-variant-inventory]");

      Object.assign(this, {
        priceDisplay: $("[data-price-display]") || $(".price-display"),
        priceEl: $("[data-current-price]") || $(".current-price"),
        comparePriceEl:
          $("[data-compare-price-amount]") || $(".compare-price__amount"),
        comparePriceRow: $("[data-compare-price-row]") || $(".compare-price"),
        savingsWrapper: $("[data-price-extras]") || $(".price-extras"),
        savingsTag: $("[data-savings-tag]") || $(".savings-tag"),
        addToCartBtn: form.querySelector(".add-to-cart"),
        addToCartText: form.querySelector("[data-add-to-cart-text]"),
        availabilityStrip: $(".availability-strip"),
        availabilityLabel: $(".availability-strip__label"),
        deliveryElement: $(".availability-strip__delivery"),
        deliveryDayEl: $("[data-delivery-day]"),
        deliveryTimeEl: $("[data-delivery-time]"),
      });

      // Get vendor from data attribute
      this.vendor = this.availabilityStrip?.dataset.vendor || "";
      this.showSavings = this.priceDisplay?.dataset.showSavings !== "false";

      this.sel.addEventListener("change", () => {
        this.onChange();
        this.sel.blur();
      });
    }

    onChange() {
      const v = this.prod?.variants?.find(
        (x) => String(x.id) === this.sel.value,
      );
      if (!v) return;
      this.price(v, this.sel.selectedOptions?.[0]?.dataset);
      this.avail(v);
      this.delivery(v);
      this.url(v);
    }

    setHidden(el, hidden) {
      if (!el) return;
      el.hidden = hidden;
      el.style.display = hidden ? "none" : "";
    }

    price({ price, compare_at_price: cp }, formatted = {}) {
      const currentPrice = Number(price) || 0;
      const compareAtPrice = Number(cp) || 0;
      const hasComparePrice = compareAtPrice > currentPrice;
      const showSavings = hasComparePrice && this.showSavings;
      const currentPriceText =
        formatted.variantPriceFormatted || formatMoney(currentPrice);
      const comparePriceText =
        formatted.variantComparePriceFormatted || formatMoney(compareAtPrice);

      if (this.priceEl) this.priceEl.textContent = currentPriceText;

      this.setHidden(this.comparePriceRow, !hasComparePrice);
      if (this.comparePriceEl) {
        this.comparePriceEl.textContent = hasComparePrice ? comparePriceText : "";
      }

      this.setHidden(this.savingsWrapper, !showSavings);
      if (this.savingsTag) {
        this.savingsTag.textContent = showSavings
          ? `-${Math.round((1 - currentPrice / compareAtPrice) * 100)}%`
          : "";
      }
    }

    avail({ id, available: av }) {
      const i = this.inv[id] || {};
      const th = parseInt(
        this.availabilityStrip?.dataset.lowStockThreshold || "5",
        10,
      );
      let st = "out";
      let txt = "Niet op voorraad";
      if (av) {
        [st, txt] =
          i.quantity > 0 && i.quantity <= th
            ? ["low", "Lage voorraad"]
            : ["in", "Op voorraad"];
      }
      if (this.availabilityStrip)
        Object.assign(this.availabilityStrip.dataset, {
          state: st,
          available: av,
          quantity: i.quantity || 0,
        });
      if (this.availabilityLabel) this.availabilityLabel.textContent = txt;
      this.updateDeliveryVisibility(av);
      if (this.addToCartBtn) this.addToCartBtn.disabled = !av;
      if (this.addToCartText)
        this.addToCartText.textContent = av
          ? "In winkelwagen"
          : "Niet op voorraad";
    }

    delivery({ sku }) {
      const days = calcBusinessDays(this.vendor, sku);
      const promiseDays = calcDeliveryPromiseDays(this.vendor, sku);
      const show = shouldShowDeliveryLine(this.vendor, sku);
      if (this.deliveryDayEl)
        this.deliveryDayEl.textContent = calcDeliveryDayName(days);
      if (this.availabilityStrip) {
        this.availabilityStrip.dataset.deliveryDays = days;
        this.availabilityStrip.dataset.deliveryPromiseDays = promiseDays;
        this.availabilityStrip.dataset.sku = sku || "";
        this.availabilityStrip.dataset.showDelivery = show;
      }
      if (this.deliveryTimeEl)
        this.deliveryTimeEl.textContent = getDeliveryText(
          promiseDays,
          this.deliveryTimeEl,
        );
      this.updateDeliveryVisibility();
    }

    updateDeliveryVisibility(isAvailable) {
      if (!this.deliveryElement || !this.availabilityStrip) return;
      const show = this.availabilityStrip.dataset.showDelivery === "true";
      const available =
        typeof isAvailable === "boolean"
          ? isAvailable
          : this.availabilityStrip.dataset.available === "true";
      this.deliveryElement.style.display = show && available ? "" : "none";
    }

    url({ id }) {
      const u = new URL(location);
      u.searchParams.set("variant", id);
      history.replaceState(null, "", u);
    }
  }

  class AddToCartForm {
    constructor(form) {
      this.form = form;
      this.button = form.querySelector(".add-to-cart");
      this.buttonText = form.querySelector("[data-add-to-cart-text]");
      this.status = form.querySelector("[data-product-form-message]");
      this.paymentButton = form.querySelector(".shopify-payment-button");
      this.paymentButtonSubmitIntent = false;
      this.paymentButtonIntentTimer = null;
      this.messageTimer = null;
      this.pending = false;
      this.availableText =
        this.buttonText?.dataset.availableText || "In winkelwagen";
      this.unavailableText =
        this.buttonText?.dataset.unavailableText || "Niet op voorraad";
      this.bindPaymentButtonIntent();
      form.addEventListener("submit", (e) => this.submit(e));
    }

    bindPaymentButtonIntent() {
      if (!this.paymentButton) return;
      const markIntent = () => {
        this.paymentButtonSubmitIntent = true;
        clearTimeout(this.paymentButtonIntentTimer);
        this.paymentButtonIntentTimer = setTimeout(() => {
          this.paymentButtonSubmitIntent = false;
        }, 1500);
      };
      this.paymentButton.addEventListener("pointerdown", markIntent, true);
      this.paymentButton.addEventListener("click", markIntent, true);
      this.paymentButton.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Enter" || event.key === " ") markIntent();
        },
        true,
      );
    }

    shouldHandleAjaxSubmit(event) {
      if (this.paymentButtonSubmitIntent) {
        this.paymentButtonSubmitIntent = false;
        clearTimeout(this.paymentButtonIntentTimer);
        return false;
      }

      const submitter = event.submitter;
      if (!submitter) return true;
      if (submitter === this.button) return true;
      if (submitter.name === "add") return true;
      return submitter.classList?.contains("add-to-cart") === true;
    }

    selectedVariantUnavailable() {
      const selectedOption = this.form.querySelector(
        'select[name="id"] option:checked',
      );
      return selectedOption?.disabled === true;
    }

    currentVariantButtonText() {
      return this.selectedVariantUnavailable()
        ? this.unavailableText
        : this.availableText;
    }

    setBusy(isBusy) {
      if (!this.button) return;
      this.button.setAttribute("aria-busy", isBusy ? "true" : "false");
      this.button.disabled = isBusy || this.selectedVariantUnavailable();
    }

    async parseAddResponse(response) {
      const text = await response.text();
      let data = {};

      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          if (!response.ok) {
            throw new Error("Kon product niet toevoegen. Probeer het opnieuw.");
          }
        }
      }

      if (!response.ok) {
        throw new Error(
          data.description ||
            data.message ||
            "Kon product niet toevoegen. Probeer het opnieuw.",
        );
      }

      return data;
    }

    async submit(e) {
      if (!this.shouldHandleAjaxSubmit(e)) return;
      e.preventDefault();
      if (this.pending || this.button?.disabled) return;
      this.pending = true;
      this.setBusy(true);
      try {
        const cartRoot = window.Shopify?.routes?.root || "/";
        const formData = new FormData(this.form);
        const sectionId = cartDrawerSectionId();
        if (sectionId) {
          formData.append("sections", sectionId);
          formData.append("sections_url", currentSectionsUrl());
        }
        const r = await fetch(`${cartRoot.replace(/\/?$/, "/")}cart/add.js`, {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });
        const d = await this.parseAddResponse(r);
        dispatchEvent(new CustomEvent("cart:item-added", { detail: d }));
        dispatchEvent(new CustomEvent("cart:open-drawer"));
        this.showMessage("Toegevoegd!", 2000);
      } catch (err) {
        const errorMessage = /failed to fetch|networkerror/i.test(
          err?.message || "",
        )
          ? "Kon product niet toevoegen. Probeer het opnieuw."
          : err.message || "Kon product niet toevoegen. Probeer het opnieuw.";
        this.showMessage(
          errorMessage,
          3000,
        );
      } finally {
        this.pending = false;
        this.setBusy(false);
      }
    }

    showMessage(message, duration) {
      clearTimeout(this.messageTimer);
      if (this.status) this.status.textContent = message;
      if (this.buttonText) {
        this.buttonText.textContent = message;
      }
      this.messageTimer = setTimeout(() => {
        if (this.buttonText?.textContent === message) {
          this.buttonText.textContent = this.currentVariantButtonText();
        }
        if (this.status?.textContent === message) this.status.textContent = "";
      }, duration);
    }
  }

  class ComplementarySlider {
    constructor(id) {
      this.slider = document.getElementById(`complementary-slider-${id}`);
      if (!this.slider) return;
      this.prev = $(`[data-slider-prev="${id}"]`);
      this.next = $(`[data-slider-next="${id}"]`);
      const dc = $(`[data-dots="${id}"]`);
      this.dots = dc ? $$("[data-dot-index]", dc) : [];
      this.items = [...this.slider.children];
      this.currentIndex = 0;
      this.perView = 2;
      this.abortController = new AbortController();
      this.cache();
      this.prev?.addEventListener(
        "click",
        () => this.go(this.currentIndex - 1),
        { signal: this.abortController.signal },
      );
      this.next?.addEventListener(
        "click",
        () => this.go(this.currentIndex + 1),
        { signal: this.abortController.signal },
      );
      this.dots.forEach((d) =>
        d.addEventListener(
          "click",
          () => this.go(parseInt(d.dataset.dotIndex, 10)),
          { signal: this.abortController.signal },
        ),
      );
      this.swipe();
      this.updateUI();
      let tm;
      window.addEventListener(
        "resize",
        () => {
          clearTimeout(tm);
          tm = setTimeout(() => {
            this.cache();
            this.go(Math.min(this.currentIndex, this.maxIdx));
          }, 150);
        },
        { passive: true, signal: this.abortController.signal },
      );
    }

    destroy() {
      this.abortController.abort();
    }

    cache() {
      if (!this.items.length) {
        this.w = 0;
        return;
      }
      const iw = this.items[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.slider).gap) || 14;
      this.w = (iw + gap) * this.perView;
    }

    get maxIdx() {
      return Math.max(0, Math.ceil(this.items.length / this.perView) - 1);
    }

    go(i) {
      this.currentIndex = Math.max(0, Math.min(i, this.maxIdx));
      this.slider.style.transform = `translate3d(${-this.currentIndex * this.w}px,0,0)`;
      this.updateUI();
    }

    updateUI() {
      this.dots.forEach((d, i) => {
        const active = i === this.currentIndex;
        d.classList.toggle("native-carousel__dot--active", active);
        if (active) d.setAttribute("aria-current", "true");
        else d.removeAttribute("aria-current");
      });
      this.prev?.toggleAttribute("disabled", this.currentIndex === 0);
      this.next?.toggleAttribute("disabled", this.currentIndex >= this.maxIdx);
    }

    swipe() {
      bindAxisLockedSwipe(this.slider, (dir) =>
        this.go(this.currentIndex + dir),
      );
    }
  }

  class ProductRecommendationsLoader {
    constructor(el) {
      this.el = el;
      this.shell = el.closest(".product-template__complementary-section");
      const ds = el.dataset,
        pid = ds.productId,
        sid = ds.sectionId,
        limit = ds.limit || 4,
        intent = ds.intent || "complementary";
      if (!pid || !sid || el.querySelector(".complementary-products")) return;
      const endpoint =
        ds.recommendationsUrl ||
        window.Shopify?.routes?.product_recommendations_url ||
        `${window.Shopify?.routes?.root || "/"}recommendations/products`;
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set("product_id", pid);
      url.searchParams.set("limit", limit);
      url.searchParams.set("intent", intent);
      url.searchParams.set("section_id", sid);

      this.setBusy(true);
      fetch(url.toString(), { headers: { Accept: "text/html" } })
        .then((r) => {
          if (!r.ok) throw new Error("Recommendations failed");
          return r.text();
        })
        .then((h) => {
          if (!h) {
            this.hideEmpty();
            return;
          }
          const c = new DOMParser()
            .parseFromString(h, "text/html")
            .querySelector("product-recommendations-complementary");
          if (!c?.innerHTML.trim() || !c.querySelector(".complementary-products")) {
            this.hideEmpty();
            return;
          }
          // Destroy old slider before replacing content
          el._slider?.destroy();
          el.innerHTML = c.innerHTML;
          el.hidden = false;
          if (this.shell) this.shell.hidden = false;
          const b = el.querySelector("[data-slider-prev]");
          if (b?.dataset.sliderPrev)
            el._slider = new ComplementarySlider(b.dataset.sliderPrev);
        })
        .catch(() => this.hideEmpty())
        .finally(() => this.setBusy(false));
    }

    setBusy(isBusy) {
      this.el.setAttribute("aria-busy", isBusy ? "true" : "false");
    }

    hideEmpty() {
      this.el._slider?.destroy();
      this.el.innerHTML = "";
      this.el.hidden = true;
      if (this.shell) this.shell.hidden = true;
    }
  }

  // Standalone delivery day calculator (runs for all products)
  function initDeliveryDays() {
    const deliveryTimeEl = $("[data-delivery-time]");
    $$(".availability-strip").forEach((strip) => {
      const dayEl = strip.querySelector("[data-delivery-day]");
      if (!dayEl) return;

      const vendor = strip.dataset.vendor || "";
      const sku = strip.dataset.sku || "";
      const businessDays = calcBusinessDays(vendor, sku);
      const promiseDays = calcDeliveryPromiseDays(vendor, sku);
      const show = shouldShowDeliveryLine(vendor, sku);
      const available = strip.dataset.available === "true";
      strip.dataset.deliveryDays = businessDays;
      strip.dataset.deliveryPromiseDays = promiseDays;
      strip.dataset.showDelivery = show;
      dayEl.textContent = calcDeliveryDayName(businessDays);
      if (deliveryTimeEl)
        deliveryTimeEl.textContent = getDeliveryText(
          promiseDays,
          deliveryTimeEl,
        );
      const deliveryLine = strip.querySelector("[data-delivery-line]");
      if (deliveryLine)
        deliveryLine.style.display = show && available ? "" : "none";
    });
  }

  class ReviewModal {
    constructor() {
      this.m = $("#reviewModal");
      if (!this.m) return;
      this.c = $(".review-modal__container", this.m);
      this.url = this.m.dataset.reviewsUrl;
      this.on = false;
      this.loaded = false;
      this.loadPromise = null;
      this.lastFocus = null;
      if (this.m.dataset.productReviewModalReady) return;
      this.m.dataset.productReviewModalReady = "true";
      document.addEventListener(
        "click",
        (e) => {
          const t = e.target.closest("[data-reviews-modal-trigger]");
          t && (e.preventDefault(), this.show(t));
        },
      );
      // Close buttons are inside the lazy-loaded container, which stops bubbling.
      // Capture the click at the modal root before that propagation guard runs.
      this.m.addEventListener(
        "click",
        (e) => {
          const b = e.target.closest("[data-modal-close]");
          if (b) this.hide();
        },
        { capture: true },
      );
      document.addEventListener("keydown", (e) => this.onKeydown(e));
      this.c?.addEventListener("click", (e) => e.stopPropagation());
    }

    getFocusable() {
      return $$(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        this.m,
      ).filter((el) => {
        if (el.closest("[hidden], [aria-hidden='true'], [inert]")) return false;
        const style = getComputedStyle(el);
        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          el.getClientRects().length > 0
        );
      });
    }

    focusInitial() {
      requestAnimationFrame(() => {
        const close = $(".review-modal__close", this.m);
        const target = close || this.getFocusable()[0] || this.c;
        target?.focus?.({ preventScroll: true });
      });
    }

    onKeydown(e) {
      if (!this.on) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.hide();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = this.getFocusable();
      if (!focusable.length) {
        e.preventDefault();
        this.c?.focus?.({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    load() {
      if (this.loaded || !this.url) return this.loadPromise || Promise.resolve();
      if (this.loadPromise) return this.loadPromise;

      this.loadPromise = (async () => {
        try {
          const res = await fetch(this.url);
          if (!res.ok) throw new Error("Failed to load reviews");
          const html = await res.text();
          const temp = document.createElement("div");
          temp.innerHTML = html;
          const content =
            temp.querySelector(".shopify-section")?.innerHTML || temp.innerHTML;
          this.c.innerHTML = content;
          this.loaded = true;
          if (this.on) this.focusInitial();
        } catch (e) {
          this.c.innerHTML = `
        <header class="review-modal__header">
          <button type="button" class="review-modal__close" aria-label="Sluit" data-modal-close>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <div class="review-modal__header-content">
            <h2 id="reviewModalTitle" class="review-modal__title">Kon reviews niet laden</h2>
            <p class="review-modal__subtitle" role="status">Probeer het later opnieuw.</p>
          </div>
        </header>
      `;
          this.loaded = true;
          if (this.on) this.focusInitial();
        } finally {
          this.loadPromise = null;
        }
      })();

      return this.loadPromise;
    }
    show(trigger) {
      if (this.on) return;
      this.lastFocus = trigger || document.activeElement;
      this.m.style.display = "flex";
      this.m.setAttribute("aria-hidden", "false");
      this.m.offsetHeight;
      this.m.classList.add("review-modal--open");
      document.body.classList.add("modal-open");
      this.on = true;
      this.focusInitial();
      if (!this.loaded) this.load();
    }
    hide() {
      if (!this.on) return;
      this.m.classList.remove("review-modal--open");
      this.m.classList.add("review-modal--closing");
      afterElementMotion(this.c, () => {
        this.m.style.display = "none";
        this.m.setAttribute("aria-hidden", "true");
        this.m.classList.remove("review-modal--closing");
        document.body.classList.remove("modal-open");
        this.on = false;
        if (this.lastFocus?.isConnected) {
          this.lastFocus.focus({ preventScroll: true });
        }
      });
    }
  }

  let initialized = false;
  const init = () => {
    if (initialized) return;
    initialized = true;

    // Calculate delivery days first (before any other initialization)
    initDeliveryDays();

    $$("[data-product-gallery]").forEach((e) => new ProductGallery(e));

    // Lightbox: desktop only (768px+)
    const lbMq = matchMedia("(min-width: 768px)");
    const lbInstances = new Map();
    const initLightboxes = () => {
      if (lbMq.matches) {
        $$("[data-product-gallery]").forEach((e) => {
          if (!lbInstances.has(e)) lbInstances.set(e, new ProductLightbox(e));
        });
      }
    };
    lbMq.addEventListener("change", initLightboxes);
    initLightboxes();
    $$(".product-form").forEach((f) => {
      new VariantSelector(f);
      new AddToCartForm(f);
    });
    $$("[data-slider-prev]").forEach((b) => {
      const id = b.dataset.sliderPrev;
      if (id && !$(`[data-slider-initialized="${id}"]`)) {
        new ComplementarySlider(id);
        b.setAttribute("data-slider-initialized", id);
      }
    });
    $$("product-recommendations-complementary").forEach((e) => {
      if (!e.dataset.recommendationsLoaded) {
        e.dataset.recommendationsLoaded = "true";
        new ProductRecommendationsLoader(e);
      }
    });
    if ($("#reviewModal")) new ReviewModal();
  };

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init, { once: true })
    : init();
})();
